import { zodResolver } from '@hookform/resolvers/zod';
import type { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { TextButton } from '@/components/common/text-button';
import { FormModal } from '@/components/feedback/modal';
import { DateField, PhoneField, SelectField, TextField } from '@/components/forms';
import { useCursorSelect } from '@/hooks/use-cursor-select';

import { listFieldInstallers } from '../api';
import { stripToAlpha } from '../format';
import { approveExternalSchema, approveInternalSchema } from '../schemas';
import type {
  ApproveExternalValues,
  ApproveInstallationBody,
  ApproveInternalValues,
  InstallerOption,
} from '../types';

import styles from './assign-installer-modal.module.scss';

export type AssignInstallerModalProps = {
  open: boolean;
  onClose: () => void;
  /** Receives the validated assignment payload; the caller owns the mutation + toasts. */
  onSubmit: (body: ApproveInstallationBody) => void;
  loading?: boolean;
  /** Pass false to keep an outside click from dismissing (replacement flow). */
  maskClosable?: boolean;
  /** Which field-installer directory to search — Installations and Replacements each hit their
   *  own real endpoint (same flat/unpaginated shape). Defaults to the Installations one. */
  fetchInstallers?: (search: string) => Promise<InstallerOption[]>;
  /** Query-key prefix so the two callers' typeaheads don't share a cache entry. */
  queryKeyPrefix?: readonly unknown[];
};

type Mode = 'internal' | 'external';

type BodyProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: ApproveInstallationBody) => void;
  loading?: boolean;
  maskClosable?: boolean;
  onSwitch: () => void;
  fetchInstallers: (search: string) => Promise<InstallerOption[]>;
  queryKeyPrefix: readonly unknown[];
};

const installerToOption = (i: InstallerOption) => ({
  value: i.id,
  label: `${i.firstName} ${i.lastName}`,
});

/** Blocks past dates on the shared "Installation On" picker (internal + external). */
const disablePast = (d: Dayjs) => d.isBefore(new Date(), 'day');

function InternalBody({
  open,
  onClose,
  onSubmit,
  loading,
  maskClosable,
  onSwitch,
  fetchInstallers,
  queryKeyPrefix,
}: BodyProps) {
  const form = useForm<ApproveInternalValues>({
    resolver: zodResolver(approveInternalSchema),
    defaultValues: { installerId: '', installationOn: '' },
    mode: 'onChange',
  });
  // Tracks the typed (alpha-stripped) search term so both the forwarded server search and the
  // visible options can be gated on a 2-character minimum: no options — server results or a
  // no-match empty state — render until the user has typed at least 2 letters.
  const [installerTerm, setInstallerTerm] = useState('');

  // The real directory is flat/unpaginated — wrapped as a single always-final "page" so
  // `useCursorSelect` still supplies debounced search + loading state for free.
  const installerSelect = useCursorSelect({
    queryKey: (search) => [...queryKeyPrefix, search],
    fetchPage: async ({ search }) => ({ data: await fetchInstallers(search), hasNextPage: false }),
    toOption: installerToOption,
  });

  return (
    <FormModal
      form={form}
      open={open}
      onClose={onClose}
      size="lg"
      centered={false}
      maskClosable={maskClosable}
      title={<h3 className={styles.title}>Approve Request</h3>}
      submitText="Assign"
      onSubmit={(values) => onSubmit(values)}
      loading={loading}
      primaryDisabled={!form.formState.isValid}
      footerLeft={
        <TextButton className={styles.switchButton} size="sm" onClick={onSwitch}>
          Assign External Employee?
        </TextButton>
      }
    >
      <div className={styles.grid}>
        <SelectField
          name="installerId"
          control={form.control}
          label="Employee"
          placeholder="Search installer"
          required
          options={installerTerm.length >= 2 ? installerSelect.options : []}
          loading={installerSelect.loading}
          // antd 6 has no loading branch for `notFoundContent` (antd/es/select/index.js:133-142),
          // so an in-flight search would otherwise read as a no-match. Both states use the shared
          // empty panel; only the copy differs.
          emptyText={installerSelect.loading ? 'Searching…' : 'No matching employees found.'}
          notFoundContent={
            installerTerm.length >= 2 ? undefined : 'Type at least 2 letters to search'
          }
          onPopupScroll={installerSelect.onPopupScroll}
          showSearch={{
            ...installerSelect.showSearch,
            onSearch: (term: string) => {
              const a = stripToAlpha(term);
              setInstallerTerm(a);
              installerSelect.showSearch.onSearch(a.length >= 2 ? a : '');
            },
          }}
        />
        <DateField
          name="installationOn"
          control={form.control}
          label="Installation On"
          required
          format="MM/DD/YYYY"
          placeholder="MM/DD/YYYY"
          disabledDate={disablePast}
          style={{ width: '100%' }}
        />
      </div>
    </FormModal>
  );
}

function ExternalBody({ open, onClose, onSubmit, loading, maskClosable, onSwitch }: BodyProps) {
  const form = useForm<ApproveExternalValues>({
    resolver: zodResolver(approveExternalSchema),
    defaultValues: { employeeName: '', contactNumber: '', installationOn: '' },
    // onTouched, not onChange: the contact-number hint belongs on focus shift, not on the first
    // keystroke of a ten-digit number. `isValid` still updates on every change, so the
    // `primaryDisabled` below is unaffected (same pairing as the shipped customer form).
    mode: 'onTouched',
  });

  return (
    <FormModal
      form={form}
      open={open}
      onClose={onClose}
      size="lg"
      centered={false}
      maskClosable={maskClosable}
      title={<h3 className={styles.title}>Approve Request</h3>}
      submitText="Assign"
      onSubmit={(values) =>
        onSubmit({
          externalInstallerName: values.employeeName,
          externalInstallerPhone: values.contactNumber,
          installationOn: values.installationOn,
        })
      }
      loading={loading}
      primaryDisabled={!form.formState.isValid}
      footerLeft={
        <TextButton className={styles.switchButton} size="sm" onClick={onSwitch}>
          Assign Internal Employee?
        </TextButton>
      }
    >
      <div className={styles.grid}>
        <TextField
          name="employeeName"
          control={form.control}
          label="Employee Name"
          placeholder="Enter name"
          maxLength={50}
          transform={stripToAlpha}
          required
        />
        <PhoneField name="contactNumber" control={form.control} label="Contact Number" required />
      </div>
      <div className={styles.grid}>
        <DateField
          name="installationOn"
          control={form.control}
          label="Installation On"
          required
          format="MM/DD/YYYY"
          placeholder="MM/DD/YYYY"
          disabledDate={disablePast}
          style={{ width: '100%' }}
        />
      </div>
    </FormModal>
  );
}

/** Presentational "Approve Request" assign step (Figma 538:333402 internal / 538:334214
 *  external): a seeded internal installer typeahead or a free-text external assignee, each with
 *  an Installation On date. Each mode is its own body with its own `useForm`, so switching mode
 *  unmounts one and mounts the other and previously-entered values are always cleared. Owns no
 *  mutation — the caller (installation approve / replacement approve) supplies `onSubmit`. */
export function AssignInstallerModal({
  open,
  onClose,
  onSubmit,
  loading,
  maskClosable,
  fetchInstallers = listFieldInstallers,
  queryKeyPrefix = ['installations', 'installers'],
}: AssignInstallerModalProps) {
  const [mode, setMode] = useState<Mode>('internal');

  const handleClose = () => {
    setMode('internal');
    onClose();
  };

  const bodyProps = {
    open,
    onClose: handleClose,
    onSubmit,
    loading,
    maskClosable,
    fetchInstallers,
    queryKeyPrefix,
  };

  if (mode === 'external') {
    return <ExternalBody {...bodyProps} onSwitch={() => setMode('internal')} />;
  }
  return <InternalBody {...bodyProps} onSwitch={() => setMode('external')} />;
}
